/* bmwmods 2026 custom: small custom elements on top of Dawn's web components.
   - "Select your BMW" garage (localStorage) and chassis picker dialog
   - Dialog helpers (menu drawer, search sheet)
   - Header disclosures (mega-menu / dropdowns)
   - Mobile sticky add-to-cart
   - Variant-dependent fragments (delivery line)
   No external libraries. */
(() => {
  const KEY = 'bm:chassis';
  const root = document.documentElement;

  /* ---------------- Garage state ---------------- */
  const store = {
    get() {
      try {
        const raw = window.localStorage.getItem(KEY);
        const value = raw ? JSON.parse(raw) : null;
        return value && value.code ? value : null;
      } catch (e) {
        return null;
      }
    },
    set(value) {
      try {
        window.localStorage.setItem(KEY, JSON.stringify(value));
      } catch (e) {
        /* storage unavailable: the choice still applies for this page view */
      }
    },
    clear() {
      try {
        window.localStorage.removeItem(KEY);
      } catch (e) {
        /* ignore */
      }
    },
  };

  const normalise = (code) => String(code || '').toUpperCase().replace(/[^A-Z0-9]/g, '');

  function fitStyleEl() {
    let el = document.getElementById('bm-fit-style');
    if (!el) {
      el = document.createElement('style');
      el.id = 'bm-fit-style';
      document.head.appendChild(el);
    }
    return el;
  }

  const fmtYears = (y) => (/-$/.test(String(y)) ? `from ${String(y).slice(0, -1)}` : String(y));

  function fitRules(code) {
    return (
      `[data-bm-fits~="${code}"] .bm-fit-yes{display:flex}` +
      `[data-bm-fits]:not([data-bm-fits~="${code}"]) .bm-fit-no{display:flex}` +
      `.bm-card[data-bm-fits~="${code}"] .bm-card__fit-yes{display:flex}` +
      `.bm-card[data-bm-fits~="${code}"] .bm-card__fits{display:none}` +
      `[data-bm-code="${code}"],[data-bm-codes~="${code}"] .bm-index__codes{color:#7A1E2B;font-weight:600}` +
      `[data-bm-row="${code}"]>*{background:rgba(122,30,43,.06)}`
    );
  }

  function applyChassis(chassis) {
    const code = chassis ? normalise(chassis.code) : '';
    const style = fitStyleEl();
    if (code) {
      root.setAttribute('data-bm-chassis', code);
      root.style.setProperty('--bm-mycode', `"${code}"`);
      style.textContent = fitRules(code);
    } else {
      root.removeAttribute('data-bm-chassis');
      root.style.removeProperty('--bm-mycode');
      style.textContent = '';
    }
    document.querySelectorAll('[data-bm-garage-code]').forEach((el) => {
      el.textContent = code;
    });
    document.querySelectorAll('[data-bm-garage-link]').forEach((a) => {
      if (chassis && chassis.url) a.href = chassis.url;
    });
    document.dispatchEvent(new CustomEvent('bm:chassis-change', { detail: chassis }));
  }

  window.bmGarage = {
    get: store.get,
    set(chassis) {
      store.set(chassis);
      applyChassis(chassis);
    },
    clear() {
      store.clear();
      applyChassis(null);
    },
  };

  /* ---------------- Dialogs ---------------- */
  function openDialog(dialog) {
    if (!dialog || dialog.open) return;
    document.querySelectorAll('dialog.bm-drawer[open]').forEach((d) => {
      if (d !== dialog) d.close();
    });
    dialog.showModal();
    root.classList.add('bm-lock');
  }

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-bm-open]');
    if (opener) {
      event.preventDefault();
      const dialog = document.getElementById(opener.dataset.bmOpen);
      openDialog(dialog);
      if (dialog && dialog.id === 'bm-search-dialog') {
        const input = dialog.querySelector('input[type="search"]');
        if (input) setTimeout(() => input.focus(), 30);
      }
      return;
    }
    const pickerOpener = event.target.closest('[data-bm-open-picker]');
    if (pickerOpener) {
      event.preventDefault();
      openPicker();
      return;
    }
    const setter = event.target.closest('[data-bm-set-chassis]');
    if (setter) {
      const code = normalise(setter.dataset.bmSetChassis);
      const row = getPickerData().find((r) => normalise(r.code) === code);
      if (row) {
        window.bmGarage.set({ code: row.code, series: row.series, body: row.body, years: row.years, url: row.url });
      }
      return;
    }
    const closer = event.target.closest('[data-bm-close]');
    if (closer) {
      const dialog = closer.closest('dialog');
      if (dialog) dialog.close();
    }
  });

  document.querySelectorAll('dialog.bm-drawer').forEach(bindDialog);
  function bindDialog(dialog) {
    if (dialog.dataset.bmBound) return;
    dialog.dataset.bmBound = 'true';
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    dialog.addEventListener('close', () => {
      if (!document.querySelector('dialog.bm-drawer[open]')) root.classList.remove('bm-lock');
    });
  }

  /* ---------------- Chassis picker ---------------- */
  let pickerData = null;
  function getPickerData() {
    if (pickerData) return pickerData;
    try {
      pickerData = JSON.parse(document.getElementById('bm-chassis-json').textContent);
    } catch (e) {
      pickerData = [];
    }
    return pickerData;
  }

  function openPicker() {
    const dialog = document.getElementById('bm-picker');
    if (!dialog) return;
    buildPicker(dialog);
    openDialog(dialog);
    const saved = store.get();
    const focusTarget =
      (saved && dialog.querySelector('[data-bm-saved-link]')) ||
      dialog.querySelector('.bm-picker__series-btn[aria-pressed="true"]') ||
      dialog.querySelector('.bm-picker__series-btn');
    if (focusTarget) focusTarget.focus();
  }

  function buildPicker(dialog) {
    const data = getPickerData();
    const seriesWrap = dialog.querySelector('[data-bm-series]');
    const saved = store.get();
    if (!seriesWrap.dataset.built) {
      const seen = [];
      data.forEach((row) => {
        if (!seen.includes(row.series)) seen.push(row.series);
      });
      seriesWrap.innerHTML = '';
      seen.forEach((series) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'bm-picker__series-btn';
        btn.textContent = series;
        btn.dataset.series = series;
        btn.setAttribute('aria-pressed', 'false');
        btn.addEventListener('click', () => selectSeries(dialog, series, true));
        seriesWrap.appendChild(btn);
      });
      seriesWrap.dataset.built = 'true';
      dialog.querySelector('[data-bm-clear]').addEventListener('click', () => {
        window.bmGarage.clear();
        renderSaved(dialog);
        dialog.querySelectorAll('.bm-picker__chassis-btn').forEach((b) => b.setAttribute('aria-pressed', 'false'));
      });
    }
    if (saved && saved.series) selectSeries(dialog, saved.series, false);
    renderSaved(dialog);
  }

  function selectSeries(dialog, series, moveFocus) {
    dialog.querySelectorAll('.bm-picker__series-btn').forEach((b) => {
      b.setAttribute('aria-pressed', String(b.dataset.series === series));
    });
    const list = dialog.querySelector('[data-bm-chassis-list]');
    const saved = store.get();
    list.innerHTML = '';
    getPickerData()
      .filter((row) => row.series === series)
      .forEach((row) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'bm-picker__chassis-btn';
        btn.setAttribute('aria-pressed', String(!!saved && normalise(saved.code) === normalise(row.code)));
        const code = document.createElement('span');
        code.className = 'bm-picker__code';
        code.textContent = row.code;
        const meta = document.createElement('span');
        meta.className = 'bm-picker__meta';
        meta.textContent = `${row.body}, ${fmtYears(row.years)}`;
        btn.append(code, meta);
        btn.setAttribute('aria-label', `${row.code}, ${series} ${row.body}, ${fmtYears(row.years)}`);
        btn.addEventListener('click', () => {
          window.bmGarage.set({
            code: row.code,
            series: row.series,
            body: row.body,
            years: row.years,
            url: row.url,
          });
          list.querySelectorAll('.bm-picker__chassis-btn').forEach((b) => b.setAttribute('aria-pressed', 'false'));
          btn.setAttribute('aria-pressed', 'true');
          renderSaved(dialog);
          const link = dialog.querySelector('[data-bm-saved-link]');
          if (link) link.focus();
          dialog.querySelector('.bm-drawer__body').scrollTo({ top: 0, behavior: 'smooth' });
        });
        list.appendChild(btn);
      });
    if (moveFocus) {
      const first = list.querySelector('button');
      if (first) first.focus();
    }
  }

  function renderSaved(dialog) {
    const saved = store.get();
    const box = dialog.querySelector('[data-bm-saved]');
    if (!box) return;
    if (saved) {
      box.classList.add('is-visible');
      box.querySelector('[data-bm-saved-code]').textContent = saved.code;
      box.querySelector('[data-bm-saved-meta]').textContent = `BMW ${saved.series} ${saved.body}, ${fmtYears(saved.years)}`;
      box.querySelector('[data-bm-saved-code]').textContent = `${saved.code} selected`;
      const link = box.querySelector('[data-bm-saved-link]');
      if (saved.url) link.href = saved.url;
    } else {
      box.classList.remove('is-visible');
    }
  }

  /* ---------------- Header disclosures ---------------- */
  if (!customElements.get('bm-disclosure')) {
    customElements.define(
      'bm-disclosure',
      class BmDisclosure extends HTMLElement {
        connectedCallback() {
          this.details = this.querySelector('details');
          this.summary = this.querySelector('summary');
          if (!this.details || !this.summary) return;
          this.summary.setAttribute('aria-expanded', 'false');
          this.hoverOpenedAt = 0;

          this.details.addEventListener('toggle', () => {
            this.summary.setAttribute('aria-expanded', String(this.details.open));
            if (this.details.open) {
              document.querySelectorAll('bm-disclosure').forEach((other) => {
                if (other !== this && other.details) other.details.open = false;
              });
            }
          });
          this.summary.addEventListener('click', (event) => {
            if (this.details.open && Date.now() - this.hoverOpenedAt < 700) event.preventDefault();
          });
          this.addEventListener('keyup', (event) => {
            if (event.key === 'Escape' && this.details.open) {
              this.details.open = false;
              this.summary.focus();
            }
          });
          this.addEventListener('focusout', (event) => {
            if (!this.contains(event.relatedTarget)) this.details.open = false;
          });
          if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
            let timer;
            this.addEventListener('mouseenter', () => {
              clearTimeout(timer);
              timer = setTimeout(() => {
                if (!this.details.open) {
                  this.details.open = true;
                  this.hoverOpenedAt = Date.now();
                }
              }, 80);
            });
            this.addEventListener('mouseleave', () => {
              clearTimeout(timer);
              timer = setTimeout(() => {
                this.details.open = false;
              }, 180);
            });
          }
        }
      }
    );
  }
  document.addEventListener('click', (event) => {
    document.querySelectorAll('bm-disclosure').forEach((el) => {
      if (el.details && el.details.open && !el.contains(event.target)) el.details.open = false;
    });
  });

  /* "/" focuses the header search */
  document.addEventListener('keydown', (event) => {
    if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;
    const tag = (event.target.tagName || '').toLowerCase();
    if (tag === 'input' || tag === 'textarea' || tag === 'select' || event.target.isContentEditable) return;
    const input = document.getElementById('bm-search-desktop');
    if (input && input.offsetParent !== null) {
      event.preventDefault();
      input.focus();
    }
  });

  /* ---------------- Mobile sticky add to cart ---------------- */
  if (!customElements.get('bm-sticky-atc')) {
    customElements.define(
      'bm-sticky-atc',
      class BmStickyAtc extends HTMLElement {
        connectedCallback() {
          this.target = document.getElementById(this.dataset.target);
          this.form = document.getElementById(this.dataset.form);
          this.button = this.querySelector('button');
          if (!this.target || !this.form || !this.button) return;
          this.setVisible(false);
          this.observer = new IntersectionObserver(
            ([entry]) => this.setVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0),
            { threshold: 0 }
          );
          this.observer.observe(this.target);
          this.button.addEventListener('click', () => {
            if (this.button.disabled) return;
            if (typeof this.form.requestSubmit === 'function') {
              const submitter = this.form.querySelector('[type="submit"]');
              this.form.requestSubmit(submitter || undefined);
            } else {
              this.form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
            }
          });
          if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => this.onVariantChange(data));
          }
        }
        disconnectedCallback() {
          if (this.observer) this.observer.disconnect();
          if (this.unsubscribe) this.unsubscribe();
        }
        setVisible(visible) {
          this.classList.toggle('is-visible', visible);
          root.classList.toggle('bm-atc-visible', visible);
          if (visible) {
            this.removeAttribute('inert');
            this.removeAttribute('aria-hidden');
          } else {
            this.setAttribute('inert', '');
            this.setAttribute('aria-hidden', 'true');
          }
        }
        onVariantChange(data) {
          if (!data || !data.html) return;
          const sectionId = this.dataset.section;
          const price = data.html.getElementById(`price-${sectionId}`);
          const priceEl = this.querySelector('[data-bm-atc-price]');
          if (price && priceEl) priceEl.innerHTML = price.innerHTML;
          const variantEl = this.querySelector('[data-bm-atc-variant]');
          if (variantEl) variantEl.textContent = data.variant ? data.variant.title : '';
          const sourceButton = data.html.getElementById(`ProductSubmitButton-${sectionId}`);
          if (sourceButton) {
            const disabled = sourceButton.hasAttribute('disabled');
            this.button.disabled = disabled;
            const label = this.button.querySelector('span');
            if (label) label.textContent = disabled ? window.variantStrings.soldOut : window.variantStrings.addToCart;
          }
        }
      }
    );
  }

  /* ---------------- Variant-dependent fragments ---------------- */
  function bindVariantSwaps() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      if (!data || !data.html) return;
      document.querySelectorAll('[data-bm-swap]').forEach((el) => {
        const source = data.html.getElementById(el.id);
        if (source) el.innerHTML = source.innerHTML;
      });
    });
  }

  /* ---------------- Init ---------------- */
  function init() {
    applyChassis(store.get());
    bindVariantSwaps();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Theme editor: re-bind dialogs added by section reloads */
  document.addEventListener('shopify:section:load', () => {
    document.querySelectorAll('dialog.bm-drawer').forEach(bindDialog);
    applyChassis(store.get());
  });
})();
